/**
 * Socket Controller
 * Handles WebSocket connections and validation for RightPlace plugin
 */

import { getSavedServerAddress } from '../services/appStatusService.js';

// Singleton instance
let socketControllerInstance = null;

export class SocketController {
	constructor(config) {
		this.config = config;
		this.wsConnection = null;
		this.isConnecting = false;
		this.isConnected = false;
		this.reconnectAttempts = 0;
		this.maxReconnectAttempts = 5;
		this.reconnectDelay = 1000; // Start with 1 second
		this.maxReconnectDelay = 30000; // Max 30 seconds
		this.heartbeatInterval = null;
		this.heartbeatTimeout = 30000; // 30 seconds
		this.serverAddress = null;
		this.isInitialized = false;
		this.periodicReconnectInterval = null;
		
		// Event listeners
		this.listeners = {
			connected: [],
			disconnected: [],
			message: [],
			error: [],
			statusUpdate: []
		};
		
		// Track which components have registered listeners
		this.registeredComponents = new Set();
	}

	/**
	 * Debug logging helper
	 */
	debugLog(...args) {
		if (this.config && this.config.debug) {
			console.log(...args);
		}
	}

	/**
	 * Initialize socket controller
	 */
	async init() {
		// Prevent duplicate initialization
		if (this.isInitialized) {
			// this.debugLog('[SocketController] Already initialized, skipping...');
			return;
		}
		
		this.debugLog('[SocketController] Initializing...');
		this.isInitialized = true;
		
		// Get server address
		await this.getServerAddress();
		
		// Start connection attempt
		this.connect();
		
		// Set up periodic reconnection attempts
		this.startPeriodicReconnection();
	}

	/**
	 * Get singleton instance of SocketController
	 * @param {object} config - Configuration object
	 * @returns {SocketController} Singleton instance
	 */
	static getInstance(config) {
		if (!socketControllerInstance) {
			socketControllerInstance = new SocketController(config);
		}
		return socketControllerInstance;
	}

	/**
	 * Reset singleton instance (for testing or cleanup)
	 */
	static resetInstance() {
		if (socketControllerInstance) {
			socketControllerInstance.isInitialized = false;
			socketControllerInstance.close();
			socketControllerInstance = null;
		}
	}

	/**
	 * Get server address from various sources
	 */
	async getServerAddress() {
		try {
			if (this.config.serverAddress) {
				this.serverAddress = this.config.serverAddress;
				this.debugLog('[SocketController] Server address from config:', this.serverAddress);
				return;
			}

			// Try to get from saved server address
			const savedServerAddress = await getSavedServerAddress(this.config);
			if (savedServerAddress) {
				this.serverAddress = savedServerAddress;
				this.debugLog('[SocketController] Server address from saved address:', this.serverAddress);
				return;
			}
		} catch (error) {
			if (this.config && this.config.debug) {
				console.warn('[SocketController] Failed to get external host URL:', error);
			}
		}

		// Fallback to config or default
		this.serverAddress = this.config.defaultServerAddress || 'http://localhost:8080';
		this.debugLog('[SocketController] Using fallback server address:', this.serverAddress);
	}

	/**
	 * Connect to WebSocket
	 */
	async connect() {
		// WebSocket handshake disabled — server-mediated sync not yet available
		this.debugLog('[SocketController] WebSocket connection disabled');
		return;
		// this.debugLog('[SocketController] Attempting to connect to:', this.serverAddress);

		try {
			// Step 1: Get site URL (remove protocol)
			const siteUrl = this.config.siteUrl.replace(/^https?:\/\//, '');
			
			// Step 2: Call handshake endpoint
			const handshakeResponse = await this.initiateHandshake(siteUrl);
			
			if (!handshakeResponse.success) {
				throw new Error(handshakeResponse.error || 'Handshake failed');
			}

			//this.debugLog('[SocketController] Handshake response:', handshakeResponse);

			// Step 3: Get encrypted handshake data from plugin
			const handshakeData = await this.getHandshakeData(handshakeResponse.connection_uuid);
			
			if (!handshakeData.success) {
				throw new Error(handshakeData.message || 'Failed to get handshake data');
			}

			// Step 4: Connect to WebSocket with encrypted data
			await this.connectWebSocket(handshakeData.connection_uuid, handshakeData.encrypted_data);

		} catch (error) {
			if (this.config && this.config.debug) {
				console.error('[SocketController] Connection failed:', error);
			}
			this.isConnecting = false;
			this.emit('error', { type: 'connection', message: error.message });
			this.scheduleReconnect();
		}
	}

	/**
	 * Initiate handshake with external server
	 */
	async initiateHandshake(siteUrl) {
		this.debugLog('[SocketController] Initiating handshake with site URL:', siteUrl);
		
		if (!this.serverAddress) {
			throw new Error('No server address available for handshake');
		}
		
		const externalServerUrl = this.serverAddress;
		const handshakeUrl = `${externalServerUrl}/v1/ws/handshake?site_url=${encodeURIComponent(siteUrl)}`;
		
		const response = await fetch(handshakeUrl, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			}
		});

		const responseData = await response.text();
		// this.debugLog('[SocketController] Handshake response text:', responseData);

		if (!response.ok) {
			throw new Error(`HTTP ${response.status}: ${responseData}`);
		}

		try {
			return JSON.parse(responseData);
		} catch (error) {
			throw new Error(`Failed to parse handshake response: ${error.message}`);
		}
	}

	/**
	 * Get handshake data from WordPress plugin
	 */
	async getHandshakeData(connectionUuid) {
		const url = `${this.config.wpJsonUrl}rightplace/v1/${this.config.urlSalt}/get-handshake`;
		
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Device-PID': this.config.devicePid,
				'X-User-PID': this.config.userPid,
				'X-Client-Signature': this.config.clientSignature,
				'DPoP': this.config.dpopToken,
				'Date': new Date().toISOString()
			},
			body: JSON.stringify({
				connection_uuid: connectionUuid
			})
		});

		const responseData = await response.json();
		// this.debugLog('[SocketController] Handshake data response:', responseData);

		if (!response.ok) {
			throw new Error(`HTTP ${response.status}: ${response.statusText}`);
		}

		return responseData;
	}

	/**
	 * Connect to WebSocket with handshake data
	 */
	async connectWebSocket(connectionUuid, encryptedData) {
		if (!this.serverAddress) {
			throw new Error('No server address available for WebSocket connection');
		}

		const externalServerUrl = this.serverAddress;
		const wsUrl = `${externalServerUrl.replace('http', 'ws')}/v1/ws?connection_uuid=${encodeURIComponent(connectionUuid)}&encrypted_data=${encodeURIComponent(encryptedData)}`;

		const CONNECTION_TIMEOUT = 10000; // 10 seconds

		return new Promise((resolve, reject) => {
			const ws = new WebSocket(wsUrl);

			// Add connection timeout
			const timeoutId = setTimeout(() => {
				if (ws.readyState !== WebSocket.OPEN) {
					ws.close();
					reject(new Error('WebSocket connection timeout'));
				}
			}, CONNECTION_TIMEOUT);

			ws.onopen = () => {
				clearTimeout(timeoutId);
				this.debugLog('[SocketController] WebSocket connected successfully');
				this.wsConnection = ws;
				this.isConnecting = false;
				this.isConnected = true;
				this.reconnectAttempts = 0;
				this.reconnectDelay = 1000; // Reset delay

				// Start heartbeat
				this.startHeartbeat();

				// Emit connected event
				this.emit('connected', { connectionUuid });
				resolve(ws);
			};

			ws.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data);
					this.debugLog('[SocketController] Received message:', data);
					this.handleMessage(data);
					this.emit('message', data);
				} catch (error) {
					if (this.config && this.config.debug) {
						console.error('[SocketController] Error parsing message:', error);
					}
				}
			};

			ws.onclose = (event) => {
				this.debugLog('[SocketController] WebSocket connection closed:', event.code, event.reason);
				this.wsConnection = null;
				this.isConnecting = false;
				this.isConnected = false;
				this.stopHeartbeat();

				// Emit disconnected event
				this.emit('disconnected', { code: event.code, reason: event.reason });

				// If connection closed with error codes, schedule reconnect
				if (event.code === 1002 || event.code === 1011) {
					this.scheduleReconnect();
				}
			};

			ws.onerror = (error) => {
				clearTimeout(timeoutId);
				if (this.config && this.config.debug) {
					console.error('[SocketController] WebSocket connection error:', error);
				}
				this.isConnecting = false;
				this.emit('error', { type: 'websocket', message: error.message || 'WebSocket error' });
				reject(new Error('WebSocket connection failed: ' + (error.message || 'Unknown error')));
			};
		});
	}

	/**
	 * Handle incoming WebSocket messages
	 */
	handleMessage(data) {
		switch (data.type) {
			case 'welcome':
				this.debugLog('[SocketController] Welcome message:', data.message);
				this.emit('statusUpdate', { type: 'welcome', message: data.message });
				break;
			case 'echo':
				this.debugLog('[SocketController] Echo response:', data.data);
				this.emit('statusUpdate', { type: 'echo', data: data.data });
				break;
			case 'connection-error':
				if (this.config && this.config.debug) {
					console.error('[SocketController] Connection error:', data.message);
				}
				this.emit('error', { type: 'connection-error', message: data.message, details: data.details });
				break;
			case 'status':
				this.debugLog('[SocketController] Status update:', data);
				this.emit('statusUpdate', { type: 'status', data: data });
				break;
			default:
				this.debugLog('[SocketController] Unknown message type:', data.type);
		}
	}

	/**
	 * Start heartbeat to keep connection alive
	 */
	startHeartbeat() {
		this.stopHeartbeat(); // Clear any existing heartbeat
		
		this.heartbeatInterval = setInterval(() => {
			if (this.isConnected && this.wsConnection) {
				this.sendMessage({ type: 'ping' });
			}
		}, this.heartbeatTimeout);
	}

	/**
	 * Stop heartbeat
	 */
	stopHeartbeat() {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}
	}

	/**
	 * Send message through WebSocket
	 */
	sendMessage(message) {
		try {
			if (this.wsConnection && this.wsConnection.readyState === WebSocket.OPEN) {
				this.wsConnection.send(JSON.stringify(message));
				return true;
			} else {
				if (this.config && this.config.debug) {
					console.warn('[SocketController] Connection not available for sending message');
				}
				return false;
			}
		} catch (error) {
			if (this.config && this.config.debug) {
				console.error('[SocketController] Error sending message:', error);
			}
			return false;
		}
	}

	/**
	 * Start periodic reconnection attempts
	 */
	startPeriodicReconnection() {
		// Clear any existing interval first
		if (this.periodicReconnectInterval) {
			clearInterval(this.periodicReconnectInterval);
		}

		// Try to reconnect every 30 seconds if not connected
		this.periodicReconnectInterval = setInterval(() => {
			if (!this.isConnected && !this.isConnecting) {
				this.debugLog('[SocketController] Periodic reconnection attempt');
				this.connect();
			}
		}, 30000);
	}

	/**
	 * Schedule reconnection with exponential backoff
	 */
	scheduleReconnect() {
		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			this.debugLog('[SocketController] Max reconnection attempts reached');
			return;
		}

		this.reconnectAttempts++;
		const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
		
		this.debugLog(`[SocketController] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
		
		setTimeout(() => {
			this.connect();
		}, delay);
	}

	/**
	 * Close WebSocket connection
	 */
	close() {
		if (this.wsConnection) {
			this.wsConnection.close();
			this.wsConnection = null;
		}
		this.isConnected = false;
		this.isConnecting = false;
		this.stopHeartbeat();

		// Clear periodic reconnection interval
		if (this.periodicReconnectInterval) {
			clearInterval(this.periodicReconnectInterval);
			this.periodicReconnectInterval = null;
		}
	}

	/**
	 * Add event listener for a specific component
	 * @param {string} componentName - Name of the component registering the listener
	 * @param {string} event - Event name
	 * @param {function} callback - Callback function
	 */
	on(componentName, event, callback) {
		// Check if component has already registered listeners
		if (this.registeredComponents.has(componentName)) {
			// this.debugLog(`[SocketController] Component ${componentName} already registered, skipping duplicate listeners`);
			return;
		}
		
		// Mark component as registered
		this.registeredComponents.add(componentName);
		
		if (this.listeners[event]) {
			this.listeners[event].push(callback);
		}
	}

	/**
	 * Remove event listener
	 */
	off(event, callback) {
		if (this.listeners[event]) {
			const index = this.listeners[event].indexOf(callback);
			if (index > -1) {
				this.listeners[event].splice(index, 1);
			}
		}
	}

	/**
	 * Remove all listeners for a specific component
	 * @param {string} componentName - Name of the component (e.g., 'AdminController', 'AdminBarController')
	 */
	offComponent(componentName) {
		// Remove component from registered set
		this.registeredComponents.delete(componentName);
		this.debugLog(`[SocketController] Removed listeners for component: ${componentName}`);
	}

	/**
	 * Emit event to all listeners
	 */
	emit(event, data) {
		if (this.listeners[event]) {
			this.listeners[event].forEach(callback => {
				try {
					callback(data);
				} catch (error) {
					if (this.config && this.config.debug) {
						console.error('[SocketController] Error in event listener:', error);
					}
				}
			});
		}
	}

	/**
	 * Get connection status
	 */
	getStatus() {
		return {
			isConnected: this.isConnected,
			isConnecting: this.isConnecting,
			serverAddress: this.serverAddress,
			reconnectAttempts: this.reconnectAttempts
		};
	}
}
